/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    RequiredValidation,
    CustomValidation,
    Select,
    ValidationMessage,
    StyledModal,
} from 'sarsaparilla';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import { getClassName } from '../../utils/validationUtils';

const propTypes = {
    issuance: PropTypes.shape({
        id: PropTypes.string,
        is_visiting_canada: PropTypes.bool,
        bwcaw_num_days_canada: PropTypes.number,
        entry_location_id: PropTypes.string,
    }),
    handleCanadaTravelChange: PropTypes.func,
    handleCanadaDaysChange: PropTypes.func,
    handleCanadaEntryPointChange: PropTypes.func,
    entryPointOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        })
    ),
    isEntryPointEnabled: PropTypes.bool,
    isDisabled: PropTypes.bool,
    formValidationChange: PropTypes.func,
    validation: PropTypes.shape({}),
    displayValidationError: PropTypes.bool,
};

const defaultProps = {
    isEntryPointEnabled: false,
    isDisabled: false,
};

export default class CanadaTravel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isCanadaTravel: props.issuance.is_visiting_canada,
            daysInCanada: +props.issuance.bwcaw_num_days_canada || 0,
            entryPointId: props.issuance.entry_location_id,
            isShowModal: false,
        };
        this.handleCanadaTravelChange = this.handleCanadaTravelChange.bind(this);
        this.handleCanadaDaysChange = this.handleCanadaDaysChange.bind(this);
        this.handleCanadaEntryPointChange = this.handleCanadaEntryPointChange.bind(this);
        this.showRulesModal = this.showRulesModal.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
    }

    onFormValidate(isInValid, error, path) {
        this.props.formValidationChange(
            `${this.props.issuance.id}_${path}`,
            isInValid ? error : null
        );
    }

    onModalClose() {
        this.setState({
            isShowModal: false,
        });
    }

    handleCanadaTravelChange(event) {
        const value = event.target.value;
        this.setState({
            isCanadaTravel: value === 'yes',
        });
        this.props.handleCanadaTravelChange(value === 'yes');
    }

    handleCanadaDaysChange(event) {
        const value = +event.target.value || 0;
        this.setState({ daysInCanada: value > 0 ? value : 0 });
        this.props.handleCanadaDaysChange(value);
    }

    handleCanadaEntryPointChange({ target: { value } }) {
        this.setState({
            entryPointId: value,
        });
        this.props.handleCanadaEntryPointChange(value);
    }

    showRulesModal(e) {
        e.preventDefault();
        this.setState({
            isShowModal: true,
        });
    }

    render() {
        const {
            issuance,
            validation,
            isDisabled,
            displayValidationError,
            entryPointOptions,
            isEntryPointEnabled,
        } = this.props;

        if (!issuance) {
            return null;
        }

        const isVisitingCanadaFailValidation = getClassName(
            get(validation, `${issuance.id}_is_visiting_canada`),
            displayValidationError
        );
        const daysVisitingCanadaFailValidation = getClassName(
            get(validation, `${issuance.id}_bwcaw_num_days_canada`),
            displayValidationError
        );
        const entryLocationCanadaFailValidation = getClassName(
            get(validation, `${issuance.id}_entry_location_id`),
            displayValidationError
        );
        return (
            <div>
                <div
                    className={
                        isVisitingCanadaFailValidation
                            ? 'permit-validation-highlight'
                            : ''
                    }
                >
                    <h2 className="h5">Traveling to Canada</h2>
                    <p>
                        All U.S. citizens who are fishing and traveling to and from Canada
                        may have special requirements and restrictions, as well as
                        additional fees and licenses that fall outside of this
                        reservation. See&nbsp;
                        <button
                            type="button"
                            onClick={this.showRulesModal}
                            className="rec-button-link"
                        >
                            Regulations &amp; Rules When Traveling to Canada
                        </button>{' '}
                        for more information.
                    </p>
                    <p>
                        <strong>
                            For trips starting in the BWCAW, do you plan to visit Canada
                            for any portion of your trip?
                        </strong>
                        <mark title="required">*</mark>
                    </p>
                    <div
                        aria-label="For trips starting in the BWCAW, do you plan to visit Canada for any portion of your trip?"
                        className="rec-form-inline-item-wrap"
                        role="group"
                    >
                        <div className="rec-form-inline-item">
                            <label
                                htmlFor="canada-travel-yes"
                                className={`rec-label-radio ${isDisabled ? 'disabled' : ''}`}
                            >
                                <input
                                    type="radio"
                                    className="rec-input-hide"
                                    id="canada-travel-yes"
                                    name="canada-travel-prompt-yes"
                                    checked={!!this.state.isCanadaTravel}
                                    value="yes"
                                    disabled={isDisabled}
                                    onChange={this.handleCanadaTravelChange}
                                />
                                <span
                                    className={`rec-input-radio ${isDisabled ? 'disabled' : ''}`}
                                />
                                Yes
                            </label>
                        </div>
                        <div className="rec-form-inline-item">
                            <label
                                htmlFor="canada-travel-no"
                                className={`rec-label-radio ${isDisabled ? 'disabled' : ''}`}
                            >
                                <input
                                    type="radio"
                                    className="rec-input-hide"
                                    id="canada-travel-no"
                                    name="canada-travel-prompt-no"
                                    checked={this.state.isCanadaTravel === false}
                                    value="no"
                                    disabled={isDisabled}
                                    onChange={this.handleCanadaTravelChange}
                                />
                                <span
                                    className={`rec-input-radio ${isDisabled ? 'disabled' : ''}`}
                                />
                                No
                            </label>
                        </div>
                        <CustomValidation
                            value={
                                isNil(this.state.isCanadaTravel)
                                    ? ''
                                    : this.state.isCanadaTravel
                            }
                            displayInSummary
                            customValidator={(value) => !isBoolean(value)}
                            onValidated={(isInValid, error) =>
                                this.onFormValidate(
                                    isInValid,
                                    error,
                                    `is_visiting_canada`
                                )
                            }
                            controlLabel="Canada Travel"
                        >
                            <ValidationMessage
                                controlName="Canada Travel"
                                message="is required."
                            />
                        </CustomValidation>
                    </div>
                </div>

                {this.state.isCanadaTravel && (
                    <div className="rec-section-outer-wrap">
                        <div className="rec-nested-wrap">
                            <div className="rec-grid-6-12">
                                <div className="rec-form-item-wrap">
                                    <label htmlFor="canadata-days">
                                        Number of nights staying in Canada?
                                        <mark title="required">*</mark>
                                    </label>
                                    <input
                                        type="number"
                                        className={daysVisitingCanadaFailValidation}
                                        min="1"
                                        id="canadata-days"
                                        defaultValue={
                                            this.state.daysInCanada
                                                ? this.state.daysInCanada
                                                : 0
                                        }
                                        disabled={isDisabled}
                                        onChange={this.handleCanadaDaysChange}
                                    />
                                    <CustomValidation
                                        value={this.state.daysInCanada || 0}
                                        displayInSummary
                                        customValidator={(value) => value < 1}
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `bwcaw_num_days_canada`
                                            )
                                        }
                                        controlLabel="Number of Nights in Canada"
                                    >
                                        <ValidationMessage
                                            controlName="Number of Nights in Canada"
                                            message="is required."
                                        />
                                    </CustomValidation>
                                </div>
                            </div>
                            {isEntryPointEnabled && (
                                <div className="rec-grid-6-12">
                                    <div className="rec-form-item-wrap">
                                        <Select
                                            className={entryLocationCanadaFailValidation}
                                            id="canada-entry-point"
                                            isRequired
                                            label="Select the lake or river where you will cross the border into Canada. If it’s not listed, select the closet one."
                                            onChange={this.handleCanadaEntryPointChange}
                                            options={entryPointOptions}
                                            placeholder="Select Entry Point"
                                            value={this.state.entryPointId || ''}
                                        />
                                        <RequiredValidation
                                            value={this.state.entryPointId || ''}
                                            displayInSummary
                                            onValidated={(isInValid, error) =>
                                                this.onFormValidate(
                                                    isInValid,
                                                    error,
                                                    `entry_location_id`
                                                )
                                            }
                                            controlLabel="Entry Point"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <hr />

                <StyledModal
                    size="md"
                    isOpen={this.state.isShowModal}
                    heading="Regulations &amp; Rules When Traveling to Canada"
                    onRequestClose={this.onModalClose}
                >
                    <div className="trip-planner-book-modal-view">
                        <div className="rec-modal-content-wrap boundarywaters">
                            <div className="lead">Entry Into Canada</div>
                            <p>
                                If your trip to the BWCAW will include entry into Canada,
                                you should contact the Canadian Border Services Agency at
                                least 6-8 weeks in advance of your trip to obtain a Remote
                                Area Border Crossing (RABC) permit. To apply for a RABC
                                Program permit, you must be a citizen or permanent
                                resident of Canada or the United States. To learn more
                                about RABC permits including eligibility, documents, fees
                                and applications, visit:&nbsp;
                                <a
                                    href="https://www.cbsa-asfc.gc.ca/prog/canpass/rabc-pfre/menu-eng.html"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    cbsa-asfc.gc.ca/prog/canpass/rabc-pfre/menu-eng.html
                                </a>
                            </p>
                            <p>
                                Permits for Quetico Provincial Park are required for
                                overnight and day use entry. Please visit&nbsp;
                                <a
                                    href="https://www.ontarioparks.com"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    ontarioparks.com
                                </a>
                                &nbsp;for permit information.
                            </p>

                            <div className="lead">Fishing In Canada</div>
                            <p>
                                If you plan on fishing in Canada, including the Canadian
                                side of the border lakes, you must have an Outdoors Card
                                and/or Canadian fishing license. Non-resident fishing
                                licenses are available online or by calling the Ontario
                                Ministry of Natural Resources at 1-800-288-1155. For more
                                information visit:&nbsp;
                                <a
                                    href="https://www.ontario.ca/page/fishing"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    ontario.ca/page/fishing
                                </a>
                            </p>

                            <div className="lead">Entry Into The U.S. From Canada</div>
                            <p>
                                U.S. citizens can present a valid U.S. Passport, Passport
                                Card, Enhanced Driver&apos;s License, Trusted Traveler
                                Program card (NEXUS, SENTRI or FAST), or Form I-872
                                American Indian Card or (when available) Enhanced Tribal
                                Card. Canadian citizens can present a valid passport,
                                Enhanced Driver&apos;s License or Trusted Traveler Program
                                card (NEXUS, SENTRI or FAST). U.S. and Canadian citizen
                                children under the age of 16 (or under 19, if traveling
                                with a school, religious group, or other youth group) need
                                only present a birth certificate or other proof of
                                citizenship. The birth certificate can be original,
                                photocopy or certified copy.
                            </p>
                            <p>
                                If you enter the United States from Canada on your BWCAW
                                trip, you must report to a Customs Border Protection (CBP)
                                officer for inspection at a port of entry or a designated
                                inspection location every time you enter the U.S. from
                                Canada by boat. Alternatively, the CBP Reporting Offsite
                                Arrival - Mobile(ROAM) application can be used by boaters
                                to check-in remotely. Download the CBP ROAM™ app prior to
                                departure from the Apple App Store or the Google Play
                                Store and visit the U.S. Customs and Border Protection
                                website for more information:&nbsp;
                                <a
                                    href="https://www.cbp.gov/travel/pleasure-boats/pleasure-boat-overview/roam"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    cbp.gov/travel/pleasure-boats/pleasure-boat-overview/roam
                                </a>
                                &nbsp;For further questions about crossing the
                                U.S./Canadian border, please call a local port of entry:
                                (218) 283-2541 (International Falls) or (218) 475-2244
                                (Grand Portage).
                            </p>

                            <div className="lead">
                                BWCAW Permits for Travel To/ From Canada
                            </div>
                            <p>
                                When your trip includes travel to or from Canada, select
                                Paddle to/from Canada or Motor to/from Canada for the
                                permit type when reserving your BWCAW permit online
                                at&nbsp;
                                <a
                                    href="https://www.recreation.gov"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    recreation.gov
                                </a>
                            </p>
                        </div>
                    </div>
                </StyledModal>
            </div>
        );
    }
}

CanadaTravel.propTypes = propTypes;
CanadaTravel.defaultProps = defaultProps;
